/**
 * ErrorDetailsButton Component
 *
 * Displays an expandable error notification with inline collapsible details.
 * Useful for debugging API failures - shows status, URL, method, response data.
 *
 * Features:
 * - Inline expandable error details (no modal)
 * - Copy-to-clipboard functionality
 * - Formatted JSON response display
 * - Timestamp for error tracking
 * - Softer colors (orange warning tone)
 * - Swipe to dismiss
 *
 * Used in toast notifications for API errors (5xx, etc.)
 *
 * @see src/services/apiErrorHandler.ts Error handling system
 */

import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse, Chip, useTheme } from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  Copy as CopyIcon,
  X as CloseIcon,
  AlertTriangle,
} from 'lucide-react';
import { AxiosError } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { closeSnackbar, SnackbarKey } from 'notistack';

interface ErrorDetailsButtonProps {
  error: AxiosError;
  snackbarKey: SnackbarKey;
}

const DetailRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.5, fontSize: '0.75rem' }}>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontWeight: 600,
        minWidth: '70px',
      }}
    >
      {label}:
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontFamily: 'monospace',
        color: 'text.primary',
        wordBreak: 'break-all',
        flex: 1,
      }}
    >
      {String(value || 'N/A')}
    </Typography>
  </Box>
);

export const ErrorDetailsButton: React.FC<ErrorDetailsButtonProps> = ({ error, snackbarKey }) => {
  const [expanded, setExpanded] = useState(false);
  const { showSuccess } = useNotification();
  const theme = useTheme();

  const handleCopy = () => {
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      timestamp: new Date().toISOString(),
      responseData: error.response?.data,
      message: error.message,
    };

    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
    showSuccess('Error details copied to clipboard');
  };

  const handleDismiss = () => {
    closeSnackbar(snackbarKey);
  };

  // Extract endpoint and method for summary
  const url = error.config?.url || 'Unknown endpoint';
  const method = error.config?.method?.toUpperCase() || 'GET';
  const status = error.response?.status || 500;
  const endpoint = url.split('?')[0]; // Remove query params

  return (
    <Box
      sx={{
        minWidth: 340,
        maxWidth: 420,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.7) 0%, rgba(30, 41, 59, 0.6) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
        border: (theme) => `1px solid ${theme.palette.warning.main}`,
        color: (theme) => theme.palette.text.primary,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 4px 24px rgba(0,0,0,0.4)'
            : '0 2px 12px rgba(0,0,0,0.08)',
        backdropFilter: 'blur(16px)',
      }}
    >
      {/* Header with summary */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        {/* Warning Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1,
            backgroundColor: 'transparent',
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={20} color={theme.palette.warning.main} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, fontSize: '0.875rem' }}>
            Server Error Occurred
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.75 }}>
            <Chip
              label={method}
              size="small"
              variant="outlined"
              sx={{
                color: (theme) => theme.palette.warning.main,
                borderColor: (theme) => theme.palette.warning.main,
                fontSize: '0.65rem',
                height: 20,
                fontWeight: 600,
                backgroundColor: 'transparent',
              }}
            />
            <Chip
              label={status}
              size="small"
              variant="outlined"
              sx={{
                color: (theme) => theme.palette.error.main,
                borderColor: (theme) => theme.palette.error.main,
                fontSize: '0.65rem',
                height: 20,
                fontWeight: 600,
                backgroundColor: 'transparent',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              color: 'text.secondary',
              wordBreak: 'break-all',
              display: 'block',
              fontFamily: 'monospace',
            }}
          >
            {endpoint}
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{
              color: 'warning.main',
              '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.08)' },
            }}
            aria-label="Toggle details"
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </IconButton>
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
            }}
            aria-label="Dismiss"
          >
            <CloseIcon size={18} />
          </IconButton>
        </Box>
      </Box>

      {/* Expandable details */}
      <Collapse in={expanded}>
        <Box
          sx={{
            backgroundColor: 'transparent',
            p: 1.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            <DetailRow label="Status" value={`${status} ${error.response?.statusText || ''}`} />
            <DetailRow label="Method" value={method} />
            <DetailRow label="URL" value={url} />
            <DetailRow label="Time" value={new Date().toLocaleTimeString()} />
          </Box>

          {error.response?.data ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}
              >
                Response:
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.04)',
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 200,
                  fontSize: '0.7rem',
                  lineHeight: 1.4,
                  color: 'text.primary',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  margin: 0,
                }}
              >
                {typeof error.response.data === 'string'
                  ? error.response.data
                  : JSON.stringify(error.response.data, null, 2)}
              </Box>
            </Box>
          ) : null}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                color: 'warning.main',
                border: (theme) => `1px solid ${theme.palette.warning.main}`,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 152, 0, 0.08)',
                },
              }}
              aria-label="Copy error details"
            >
              <CopyIcon size={16} />
            </IconButton>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
